import { useState, type ReactNode } from 'react';
import { api, errorMessage } from '../api';
import { PERMISSIONS } from '../constants';
import { formatDateTime, formatRelative, jsonText } from '../format';
import { useAsyncData } from '../useAsyncData';
import type { CloudEventsResponse, CloudStatusResponse, OkResponse, ToastKind } from '../types';
import { EmptyBox, ErrorBox, HintBox, Loading } from '../components/Feedback';

export interface CloudPageProps {
  permissions: string[];
  /** Erhöht sich bei Datenänderungen (Aufgaben, Rollen, Spieler, Benutzer). */
  revision: number;
  /** Erhöht sich bei `cloud.status`-Nachrichten aus dem WebSocket. */
  liveRevision: number;
  onToast: (kind: ToastKind, text: string) => void;
}

export function CloudPage({
  permissions,
  revision,
  liveRevision,
  onToast,
}: CloudPageProps): ReactNode {
  const canView = permissions.includes(PERMISSIONS.TASK_VIEW);
  const canSeeEvents = permissions.includes(PERMISSIONS.USER_MANAGE);
  const canSync = permissions.includes(PERMISSIONS.ROLE_MANAGE);

  const [syncing, setSyncing] = useState(false);

  const statusState = useAsyncData(
    () => api.get<CloudStatusResponse>('/api/cloud/status'),
    [revision, liveRevision],
    canView,
  );
  const eventsState = useAsyncData(
    () => api.get<CloudEventsResponse>('/api/cloud/events', { limit: 60 }),
    [revision, liveRevision],
    canSeeEvents,
  );

  if (!canView) {
    return <ErrorBox message="Für die Cloud-Übersicht fehlt das Recht task.view." />;
  }

  const status = statusState.data?.status ?? null;
  const events = eventsState.data?.events ?? [];

  const sync = async (): Promise<void> => {
    setSyncing(true);
    try {
      await api.post<OkResponse>('/api/cloud/sync', {});
      onToast('success', 'Synchronisierung angefordert — die Cloud sendet die Rollen neu.');
    } catch (cause: unknown) {
      onToast('error', errorMessage(cause));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="page">
      <div className="board-toolbar">
        <div className="toolbar-left">
          <h2 className="page-title">Cloud</h2>
          <span className="muted small">Verbindung, Rollen-Sync und letzte Pakete</span>
        </div>
        <div className="toolbar-right">
          <button
            type="button"
            className="button button-ghost button-small"
            onClick={() => {
              statusState.reload();
              eventsState.reload();
            }}
          >
            Aktualisieren
          </button>
          <button
            type="button"
            className="button button-primary"
            disabled={!canSync || syncing}
            title={canSync ? undefined : 'Dafür fehlt das Recht role.manage.'}
            onClick={() => void sync()}
          >
            {syncing ? 'Synchronisiere …' : 'Rollen neu synchronisieren'}
          </button>
        </div>
      </div>

      {!canSync && (
        <HintBox text="Zum Synchronisieren der Rollen fehlt das Recht role.manage." />
      )}

      {statusState.error !== null && (
        <ErrorBox message={statusState.error} onRetry={() => statusState.reload()} />
      )}
      {statusState.loading && status === null && <Loading label="Cloud-Status laden …" />}

      {status !== null && (
        <section className="status-card">
          <header className="status-card-head">
            <h3>Verbindungsstatus</h3>
            <span className={status.connected ? 'badge badge-ok' : 'badge badge-off'}>
              {status.status}
            </span>
          </header>
          <div className="status-grid">
            <StatusItem label="Verbunden" value={status.connected ? 'ja' : 'nein'} good={status.connected} />
            <StatusItem label="Registriert" value={status.registered ? 'ja' : 'nein'} good={status.registered} />
            <StatusItem label="Host" value={`${status.host}:${status.port}`} />
            <StatusItem label="Bekannte Ranks" value={String(status.knownRanks)} />
            <StatusItem label="Bekannte Spieler-Ranks" value={String(status.knownPlayerRanks)} />
            <StatusItem label="Pakete rein" value={String(status.packetsIn)} />
            <StatusItem label="Pakete raus" value={String(status.packetsOut)} />
            <StatusItem label="Reconnect-Versuche" value={String(status.reconnectAttempts)} />
            <StatusItem
              label="Nächster Versuch"
              value={status.nextRetryInMs === null ? '—' : `${Math.round(status.nextRetryInMs / 1000)} s`}
            />
            <StatusItem
              label="Zuletzt verbunden"
              value={
                status.lastConnectedAt === null
                  ? '—'
                  : `${formatDateTime(status.lastConnectedAt)} (${formatRelative(status.lastConnectedAt)})`
              }
            />
            <StatusItem
              label="Zuletzt getrennt"
              value={status.lastDisconnectedAt === null ? '—' : formatDateTime(status.lastDisconnectedAt)}
            />
          </div>
          {status.lastError !== null && status.lastError !== '' && (
            <div className="feedback feedback-error">
              <span className="feedback-icon" aria-hidden="true">
                !
              </span>
              <span>Letzter Fehler: {status.lastError}</span>
            </div>
          )}
        </section>
      )}

      <section className="section-block">
        <div className="detail-section-head">
          <h3>Letzte Ereignisse</h3>
          <span className="muted small">Neueste zuerst, maximal 60 Einträge</span>
        </div>

        {!canSeeEvents && (
          <HintBox text="Die Ereignisliste erfordert das Recht user.manage." />
        )}
        {eventsState.error !== null && (
          <ErrorBox message={eventsState.error} onRetry={() => eventsState.reload()} />
        )}
        {eventsState.loading && eventsState.data === null && <Loading label="Ereignisse laden …" />}
        {canSeeEvents && !eventsState.loading && eventsState.error === null && events.length === 0 && (
          <EmptyBox text="Noch keine Cloud-Ereignisse aufgezeichnet." />
        )}

        {events.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Zeit</th>
                  <th>Richtung</th>
                  <th>Typ</th>
                  <th>Nutzlast</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id}>
                    <td className="muted small nowrap">{formatDateTime(event.createdAt)}</td>
                    <td>
                      <span className={event.direction === 'in' ? 'badge badge-in' : 'badge badge-out'}>
                        {event.direction === 'in' ? 'eingehend' : 'ausgehend'}
                      </span>
                    </td>
                    <td className="mono">{event.type}</td>
                    <td>
                      <pre className="payload">{jsonText(event.payload)}</pre>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function StatusItem({
  label,
  value,
  good,
}: {
  label: string;
  value: string;
  good?: boolean;
}): ReactNode {
  return (
    <div className="status-item">
      <span className="status-item-label">{label}</span>
      <span
        className={
          good === undefined ? 'status-item-value' : good ? 'status-item-value ok' : 'status-item-value bad'
        }
      >
        {value}
      </span>
    </div>
  );
}
